import { Keypair, VersionedTransaction, PublicKey } from '@solana/web3.js';
import {
  AuthorizationError,
  UniversalKeyPair,
  TaggedKeyPair,
  SignerProvider,
  KmsPayload,
  BaseResponse

} from '../types';
import { base64ToBytes, bytesToBase64, hexToBytes, base64ToUtf8 } from '../utils/encoding';
import { PrivyInternalSignRequest } from '../types/privy';
import { signWithDecryptedAuthorizationKey, decryptTurnkeyCredentials, getPublicKeyFromDecryptedData, signWithApiKey  } from '@sqds/grid-crypto';

/**
 * Sign Request
 * @property {TaggedKeyPair[] | Keypair[]} sessionSecrets - Secrets generated via generateSessionSecrets()
 * @property {any[]} [session] - Optional session data from authentication via initAuth()
 * @property {TransactionPayload} transactionPayload - Transaction data to be signed and sent
 * @property {SignerProvider} provider - Provider to use for signing (privy, turnkey)
 */
export interface SignRequest {
  sessionSecrets: TaggedKeyPair[] | Keypair[];
  session?: any[];  // Session data from authentication
  transactionPayload: TransactionPayload;
  provider?: SignerProvider;
}

/**
 * Request to sign and send a transaction to the blockchain.
 * @property {TaggedKeyPair[] | Keypair[]} sessionSecrets - Cryptographic keys for signing
 * @property {any[]} [session] - Optional session data from authentication
 * @property {TransactionPayload} transactionPayload - Transaction data to be signed and sent
 * @property {string} address - Target address for the transaction
 */
export interface SignAndSendRequest {
  sessionSecrets: TaggedKeyPair[] | Keypair[];
  session?: any[];  // Session data from authentication
  transactionPayload: TransactionPayload;
  address: string;
}

/**
 * @hidden
 */
export interface SignatureResult {
  provider: SignerProvider;
  signature: string;
  publicKey: string;
  timestamp: number;
}

/**
 * @hidden
 */
export interface SignResult {
  signatures: SignatureResult[];
  payload?: string;
  aggregatedSignature?: string;
}

/**
 * Transaction payload containing the transaction data and required signatures.
 * @property {string} transaction - Serialized transaction data
 * @property {string[]} transactionSigners - Array of signer public keys
 * @property {KmsPayload[]} kmsPayloads - Key management service payloads for signing
 *
 * @example
 * ```typescript
 * const payload: TransactionPayload = {
 *   transaction: 'AQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACAAQABBCk...',
 *   transactionSigners: ['5FHwkrdxntdK24hgQU8qgBjn35Y1zwhz1GZwCkP2UJnM'],
 *   kmsPayloads: [
 *     {
 *       provider: 'privy',
 *       address: '5FHwkrdxntdK24hgQU8qgBjn35Y1zwhz1GZwCkP2UJnM',
 *       payload: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
 *     }
 *   ]
 * };
 * ```
 */
export interface TransactionPayload {
  transaction: string;
  transactionSigners: string[];
  kmsPayloads: KmsPayload[];
}

export interface TransactionResponse extends BaseResponse <TransactionPayload> {}
/**
 * @hidden
 */
enum KmsProvider {
  PRIVY = 'privy',
  TURNKEY = 'turnkey'
}

/**
 * @hidden
 */
export interface TurnkeyKmsSignedPayload {
  public_key: string;
  signature: string;
  timestamp_ms: string;
}

/**
 * @hidden
 */
export interface PrivyKmsSignedPayload {
  signature: string;
}

/**
 * @hidden
 */
export interface KmsSingature {
  provider: KmsProvider;
  kms_signed_payload?: TurnkeyKmsSignedPayload | PrivyKmsSignedPayload;
  signature?: string;
}

/**
 * Result of a transaction signing operation.
 * @property {string} transaction - The signed transaction data
 * @property {KmsSingature[]} kmsPayloads - Array of KMS signature payloads
 */
export interface TransactionResult {
  transaction: string;
  kmsPayloads: KmsSingature[];
}

/**
 * @hidden
 */
export class SigningManager {
  constructor() {
  }

  /**
   * Get signers that haven't signed the transaction yet
   */
  private getUnsignedSigners(transaction: string, requestedSigners: string[]): string[] {
    try {
      const transactionBytes = base64ToBytes(transaction);
      const versionedTransaction = VersionedTransaction.deserialize(transactionBytes);

      const accountKeys = versionedTransaction.message.staticAccountKeys;
      const signatures = versionedTransaction.signatures;

      console.log(`🔍 Transaction has ${signatures.length} signature slots for ${accountKeys.length} account keys`);

      return requestedSigners.filter(signer => {
        try {
          const signerPubkey = new PublicKey(signer);
          const signerIndex = accountKeys.findIndex(key => key.equals(signerPubkey));

          if (signerIndex === -1) {
            console.log(`❌ Signer ${signer} not found in transaction account keys`);
            return false;
          }

          const signature = signatures[signerIndex];

          // Check if signature is actually present and valid
          const isValidSignature = signature !== null &&
                                  signature.length > 0 &&
                                  !this.isEmptySignature(signature);

          console.log(`🔍 Signer ${signer} at index ${signerIndex}: signature ${isValidSignature ? 'present' : 'missing'}`);

          // Return true if signer needs to sign (signature is missing or empty)
          return !isValidSignature;
        } catch (error) {
          // Invalid public key, skip
          console.warn(`Invalid public key in signer list: ${signer}`);
          return false;
        }
      });
    } catch (error) {
      // If we can't deserialize the transaction, return all signers (fallback behavior)
      console.warn('Failed to deserialize transaction for signature checking, falling back to all signers');
      return requestedSigners;
    }
  }

  /**
   * Check if a signature is empty (all zeros or placeholder)
   */
  private isEmptySignature(signature: Uint8Array): boolean {
    // Check if signature is all zeros (64 bytes for Ed25519)
    if (signature.length !== 64) return true;

    return signature.every(byte => byte === 0);
  }

  /**
   * Check if a specific public key has already signed the transaction
   */
  private hasPublicKeySigned(transaction: string, pubkey: string): boolean {
    try {
      const transactionBytes = base64ToBytes(transaction);
      const versionedTransaction = VersionedTransaction.deserialize(transactionBytes);

      const accountKeys = versionedTransaction.message.staticAccountKeys;
      const signatures = versionedTransaction.signatures;

      const signerPubkey = new PublicKey(pubkey);
      const signerIndex = accountKeys.findIndex(key => key.equals(signerPubkey));

      if (signerIndex === -1) return false;

      const signature = signatures[signerIndex];
      const isValidSignature = signature !== null &&
                              signature.length > 0 &&
                              !this.isEmptySignature(signature);

      return isValidSignature;
    } catch (error) {
      return false;
    }
  }

  /**
   * Smart signing with automatic provider selection based on session
   */
  async sign(request: SignRequest): Promise<TransactionResult> {
    const provider = request.provider;
    // Support both 'payload' and 'transaction' for backward compatibility
    const transaction = request.transactionPayload.transaction;
    const transactionSigners = request.transactionPayload.transactionSigners;
    const kmsPayloads = request.transactionPayload.kmsPayloads;
    const { sessionSecrets, session } = request;

    // Validate inputs
    if (!transaction && !kmsPayloads) {
      throw new AuthorizationError('Payload is required for signing', 'MISSING_PAYLOAD');
    }

    if (!sessionSecrets || sessionSecrets.length === 0) {
      throw new AuthorizationError('At least one keypair is required for signing', 'NO_KEYPAIRS');
    }

    const signatures: KmsSingature[] = [];
    let signedTransaction = transaction;

    // Filter out signers that have already signed the transaction
    const unsignedSigners = this.getUnsignedSigners(transaction, transactionSigners);

    console.log(`🔍 Signing check: ${transactionSigners.length} total signers, ${unsignedSigners.length} unsigned signers`);
    if (transactionSigners.length > unsignedSigners.length) {
      const alreadySigned = transactionSigners.filter(signer => !unsignedSigners.includes(signer));
      console.log(`✅ Already signed by: ${alreadySigned.join(', ')}`);
    }

    if (unsignedSigners.length === 0) {
      console.log('✅ All required signers have already signed the transaction');
      return {
        transaction: signedTransaction,
        kmsPayloads: signatures
      };
    }

    await Promise.all(unsignedSigners.map(async signer => {
      
      if (kmsPayloads && kmsPayloads.length > 0) {
        const kmsPayload = kmsPayloads.find(kp => kp.address === signer) || kmsPayloads.find(kp => kp.provider === provider);  

        if (kmsPayload) {
          switch (kmsPayload.provider) {
            case 'privy':
              const keypair = sessionSecrets.find(kp => (kp as TaggedKeyPair).provider === 'privy');
              
              const signature = await this.signWithPrivyProvider(kmsPayload.payload, keypair as TaggedKeyPair, session);
              signatures.push({
                provider: KmsProvider.PRIVY,
                signature
              });
              break;
            case 'turnkey':
              const turnkeyKeypair = sessionSecrets.find(kp => (kp as TaggedKeyPair).provider === 'turnkey');
              const turnkeySignature = await this.signWithTurnkeyProvider(kmsPayload.payload, turnkeyKeypair as any, session, Date.now());
              signatures.push({
                provider: KmsProvider.TURNKEY,
                kms_signed_payload: JSON.parse(turnkeySignature.signature) as TurnkeyKmsSignedPayload
              });
              break;
            case 'passkey':
              const passkeyKeypair = sessionSecrets.find(kp => (kp as TaggedKeyPair).provider === 'passkey') as TaggedKeyPair;
              const secretKeyBytes = hexToBytes(passkeyKeypair.privateKey!);
              // Create Solana Keypair from the secret key bytes
              const convertedPasskeyKeypair = Keypair.fromSecretKey(secretKeyBytes);
                if (!convertedPasskeyKeypair) {
                  throw new AuthorizationError(
                    `No keypair found for external signer: ${signer}`,
                    'KEYPAIR_NOT_FOUND'
                  );
                }
                signedTransaction = await this.signWithSolanaProvider(transaction, convertedPasskeyKeypair);
              break;
            case 'external':
              const solanaKeypair = sessionSecrets.find(kp => (kp as Keypair).publicKey.toString() === signer) as Keypair;
              if (!solanaKeypair) {
                throw new AuthorizationError(
                  `No keypair found for external signer: ${signer}`,
                  'KEYPAIR_NOT_FOUND'
                );
              }
              signedTransaction = await this.signWithSolanaProvider(transaction, solanaKeypair);
              break;
            default:
              throw new AuthorizationError(
                `Unsupported provider for signing`,
                'UNSUPPORTED_PROVIDER'
              );
              
          }
        }
      } else {
        const solanaKeypair = sessionSecrets.find(kp => (kp as Keypair).publicKey.toString() === signer) as Keypair;
        if (!solanaKeypair) {
          throw new AuthorizationError(
            `No keypair found for external signer: ${signer}`,
            'KEYPAIR_NOT_FOUND'
          );
        }
        signedTransaction = await this.signWithSolanaProvider(transaction, solanaKeypair);
      }
    }));

    const transactionResult: TransactionResult = {
      transaction: signedTransaction,
      kmsPayloads: signatures
    };

    return transactionResult;
  }

  /**
   * Privy signing implementation using local PrivyProvider
   */
  private async signWithPrivyProvider(
    transaction: any,
    keypair: TaggedKeyPair,
    session: any[] | undefined,
    // timestamp: number
  ): Promise<string> {
    // Check if we have session-based signing available
    if (session && session.length > 0) {
      // Find Privy session
      const privySession = session.find(s => s.session?.Privy);
      if (privySession?.session?.Privy?.session?.encrypted_authorization_key) {
        // Use encrypted authorization key for signing
        const authKey = privySession.session.Privy.session.encrypted_authorization_key;
        const privyRequest: PrivyInternalSignRequest = {
          transaction: typeof transaction === 'string' ? transaction : JSON.stringify(transaction),
          sessionSecrets: [keypair],
          signers: [keypair],
          authorizationKey: authKey
        };

        // Extract the Privy signing logic directly
        try {
          // Decrypt the authorization key using ChaCha20-Poly1305 HPKE
          // Dynamic import for platform-specific crypto
          const { hpkeChachaDecryptRN } = await import('@sqds/grid-crypto');
          const decryptedBytes = await hpkeChachaDecryptRN(
            authKey,
            keypair.privateKey
          );
          const decryptedAuthKey = new TextDecoder().decode(decryptedBytes);
          // Sign the payload directly
          const signature = signWithDecryptedAuthorizationKey(transaction, decryptedAuthKey);
          return signature;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          throw new AuthorizationError(
            `Privy signing failed: ${errorMessage}`,
            'SIGNING_FAILED'
          );
        }
      }
    }

    // Fallback to local signing if no session
    if (!keypair.privateKey) {
      throw new AuthorizationError(
        'Private key required for Privy signing without session',
        'MISSING_PRIVATE_KEY'
      );
    }

    // Use direct signing for fallback
    const signature = signWithDecryptedAuthorizationKey(typeof transaction === 'string' ? transaction : JSON.stringify(transaction), keypair.privateKey);
    
    return signature;
  }

  /**
   * Turnkey signing implementation using local TurnkeyProvider
   */
  private async signWithTurnkeyProvider(
    transaction: string,
    keypair: UniversalKeyPair,
    session: any[] | undefined,
    timestamp: number
  ): Promise<SignatureResult> {
    // Validate inputs
    if (!keypair.privateKey) {
      throw new AuthorizationError(
        'Private key required for Turnkey signing',
        'MISSING_PRIVATE_KEY'
      );
    }

    if (!session || session.length === 0) {
      throw new AuthorizationError(
        'Session required for Turnkey signing',
        'MISSING_SESSION'
      );
    }

    const turnkeySession = session.find(s => s.provider === 'turnkey')?.session?.Turnkey;
    if (!turnkeySession) {
      throw new AuthorizationError(
        'Turnkey session not found',
        'INVALID_SESSION'
      );
    }

    const credentials = turnkeySession.credential_bundle;
    if (!credentials) {
      throw new AuthorizationError(
        'Credential bundle not found in session',
        'MISSING_CREDENTIALS'
      );
    }

    try {
      // Decode the base64 payload to get the Turnkey request body
      const requestBody = JSON.parse(base64ToUtf8(transaction));

      // Decrypt credentials to get API key
      const decryptedData = await decryptTurnkeyCredentials(credentials, keypair.privateKey);

      // Get public key and sign the request
      const publicKey = await getPublicKeyFromDecryptedData(decryptedData);
      const signature = await signWithApiKey({
        payload: JSON.stringify(requestBody),
        privateKey: decryptedData,
      });

      // Return stamped request
      return {
        provider: 'turnkey',
        signature: JSON.stringify({
          public_key: publicKey,
          signature: signature,
          timestamp_ms: requestBody.timestampMs || timestamp.toString(),
        }),
        publicKey: keypair.publicKey,
        timestamp
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new AuthorizationError(
        `Turnkey signing failed: ${errorMessage}`,
        'SIGNING_FAILED'
      );
    }
  }

  /**
   * Solana signing implementation
   */
  private async signWithSolanaProvider(
    transaction: string,
    keypair: Keypair
  ): Promise<string> {
    try {
      const transactionBytes = base64ToBytes(transaction);
      const versionedTransaction = VersionedTransaction.deserialize(transactionBytes);
      versionedTransaction.sign([keypair]);
      const serializedTransaction = versionedTransaction.serialize();
      return bytesToBase64(serializedTransaction);
    } catch (error: any) {
      throw new AuthorizationError(
        `Failed to sign with Solana provider: ${error.message}`,
        'SIGNING_FAILED'
      );
    }
  }

  /**
   * Passkey signing implementation
   */
  private async signWithPasskeyProvider(
    transaction: any,
    keypair: TaggedKeyPair,
    timestamp: number
  ): Promise<SignatureResult> {
    throw new AuthorizationError(
      'Passkey signing not yet implemented',
      'NOT_IMPLEMENTED'
    );
  }

  /**
   * Select keypairs based on session data
   * Prioritizes signers by role (primary > backup) and matches addresses with authenticated wallets
   */
  private selectKeypairsFromSession(
    keypairs: TaggedKeyPair[],
    session?: any[],
    transaction_signers?: string[]
  ): TaggedKeyPair[] {
    
    // If transaction_signers are provided, use them to determine which providers to use
    if (transaction_signers && transaction_signers.length > 0 && session && session.length > 0) {
      return this.selectKeypairsFromTransactionSigners(keypairs, session, transaction_signers);
    }
    
    // If no session, return all keypairs (for non-session based signing like Solana)
    if (!session || session.length === 0) {
      // Filter for non-session based providers
      const nonSessionKeypairs = keypairs.filter(kp => 
        kp.provider === 'solana' || kp.provider === 'passkey'
      );
      return nonSessionKeypairs.length > 0 ? nonSessionKeypairs : keypairs;
    }

    let selected: TaggedKeyPair[] = [];

    // Check each session entry and match with appropriate keypairs
    for (const sessionEntry of session) {
      
      // Check for Privy session
      if (sessionEntry.provider === 'privy' || sessionEntry.session?.Privy) {
        const privySession = sessionEntry.session?.Privy;
        const authenticatedWallets = privySession?.session?.wallets || [];
        
        
        // Find keypairs that match authenticated Privy wallet addresses
        const matchingPrivyKeypairs = keypairs.filter(kp => {
          if (kp.provider !== 'privy') return false;
          
          // Check if keypair address matches any authenticated wallet address
          const matchesWallet = authenticatedWallets.some((wallet: any) => 
            wallet.address === kp.publicKey || wallet.public_key === kp.publicKey
          );
          
          return matchesWallet;
        });
        
        // Prioritize by role: primary > backup > others
        const sortedPrivyKeypairs = matchingPrivyKeypairs.sort((a, b) => {
          const roleOrder = { primary: 1, backup: 2, solana: 3, passkey: 4 };
          return (roleOrder[a.tag] || 99) - (roleOrder[b.tag] || 99);
        });
        
        if (sortedPrivyKeypairs.length > 0) {
          const selectedPrivyKeypair = sortedPrivyKeypairs[0];
          if (!selected.includes(selectedPrivyKeypair)) {
            selected.push(selectedPrivyKeypair);
          }
        }
      }
      
      // Check for Turnkey session
      if (sessionEntry.provider === 'turnkey' || sessionEntry.session?.Turnkey) {
        const turnkeySession = sessionEntry.session?.Turnkey;
        
        // For Turnkey, we need to match based on provider and available session data
        const matchingTurnkeyKeypairs = keypairs.filter(kp => kp.provider === 'turnkey');
        
        // Prioritize by role: primary > backup > others
        const sortedTurnkeyKeypairs = matchingTurnkeyKeypairs.sort((a, b) => {
          const roleOrder = { primary: 1, backup: 2, solana: 3, passkey: 4 };
          return (roleOrder[a.tag] || 99) - (roleOrder[b.tag] || 99);
        });
        
        if (sortedTurnkeyKeypairs.length > 0) {
          const selectedTurnkeyKeypair = sortedTurnkeyKeypairs[0];
          if (!selected.includes(selectedTurnkeyKeypair)) {
            selected.push(selectedTurnkeyKeypair);
          }
        }
      }
      
      // Check for other providers...
    }

    // If no session-matched keypairs found, check for EOA wallets
    if (selected.length === 0) {
      const eoaKeypairs = keypairs.filter(kp => 
        kp.provider === 'solana' || kp.provider === 'external'
      );
      if (eoaKeypairs.length > 0) {
        selected = eoaKeypairs;
      }
    }

    // Default to primary role keypairs if no selection made
    if (selected.length === 0) {
      const primaryKeypairs = keypairs.filter(kp => kp.tag === 'primary');
      selected = primaryKeypairs.length > 0 ? primaryKeypairs : keypairs;
    }

    return selected;
  }

  /**
   * Select keypairs based on transaction_signers addresses
   * Matches transaction_signers to session wallet addresses to determine providers
   */
  private selectKeypairsFromTransactionSigners(
    keypairs: TaggedKeyPair[],
    session: any[],
    transactionSigners: string[]
  ): TaggedKeyPair[] {
    const selected: TaggedKeyPair[] = [];
    
    // For each required transaction signer
    for (const signerAddress of transactionSigners) {
      
      // Search through all sessions to find which provider has this signer
      let providerFound: SignerProvider | null = null;
      
      for (const sessionEntry of session) {
        // Check Privy session
        if (sessionEntry.provider === 'privy' || sessionEntry.session?.Privy) {
          const privySession = sessionEntry.session?.Privy;
          const authenticatedWallets = privySession?.session?.wallets || [];
          
          const hasThisSigner = authenticatedWallets.some((wallet: any) => 
            wallet.address === signerAddress
          );
          
          if (hasThisSigner) {
            providerFound = 'privy';
            break;
          }
        }
        
      }
      
      if (providerFound) {
        // Find a keypair for this provider
        const matchingKeypairs = keypairs.filter(kp => kp.provider === providerFound);
        
        // Prioritize by role: primary > backup > others
        const sortedKeypairs = matchingKeypairs.sort((a, b) => {
          const roleOrder = { primary: 1, backup: 2, solana: 3, passkey: 4 };
          return (roleOrder[a.tag] || 99) - (roleOrder[b.tag] || 99);
        });
        
        if (sortedKeypairs.length > 0) {
          const selectedKeypair = sortedKeypairs[0];
          
          if (!selected.includes(selectedKeypair)) {
            selected.push(selectedKeypair);
          }
        } else {
          console.log(`⚠️  No ${providerFound} keypairs available for signer ${signerAddress}`);
        }
      } else {
        console.log(`❌ Could not find provider for signer ${signerAddress}`);
      }
    }
    
    return selected;
  }

  /**
   * Aggregate multiple signatures (for future use)
   */
  private aggregateSignatures(signatures: SignatureResult[]): string | undefined {
    // This would implement signature aggregation logic
    // For now, return undefined
    return undefined;
  }
}